import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import baseUrl from "../../api/utils";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../../assets/css/invoicestyle.css";

const MySwal = withReactContent(Swal);

const TABS = [
  { key: "all",      label: "All Items" },
  { key: "category", label: "Category" },
  { key: "unit",     label: "Unit" },
];

const Units = () => {
  const token    = sessionStorage.getItem("token");
  const role     = sessionStorage.getItem("role");
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────
  const [units,         setUnits]         = useState([]);
  const [conversions,   setConversions]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [convLoading,   setConvLoading]   = useState(false);
  const [selectedUnit,  setSelectedUnit]  = useState(null);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [activeTab,     setActiveTab]     = useState("unit");
  const [openMenuId,    setOpenMenuId]    = useState(null);

  // Add Unit Modal (Name + Symbol only now)
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [addLoading,    setAddLoading]    = useState(false);
  const [addForm,       setAddForm]       = useState({ name: "", symbol: "" });

  // Edit Unit Modal (Name + Symbol only now)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading,   setEditLoading]   = useState(false);
  const [editUnit,      setEditUnit]      = useState(null);
  const [editForm,      setEditForm]      = useState({ name: "", symbol: "" });

  // Add Conversion Modal
  const [showConvModal, setShowConvModal] = useState(false);
  const [convAddLoading, setConvAddLoading] = useState(false);
  const [convForm,      setConvForm]      = useState({
    baseRate: "1", secondaryUnitId: "", rate: ""
  });

  const menuRef = useRef(null);

  // ── Close menu on outside click ──────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Fetch all units on mount ─────────────────────────────
  useEffect(() => {
    fetchUnits();
  }, []);

  // ── Fetch conversions when unit selected ──────────────────
  useEffect(() => {
    if (selectedUnit) {
      fetchConversionsByUnit(selectedUnit.id);
    } else {
      setConversions([]);
    }
  }, [selectedUnit]);

  // ── API: Get all units ───────────────────────────────────
  const fetchUnits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/getUnits`, {
        headers: { Authorization: token },
      });
      setUnits(res.data);
      if (res.data.length > 0) setSelectedUnit(res.data[0]);
    } catch (err) {
      MySwal.fire("Error", "Failed to load units!", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── API: Get conversions for selected unit ───────────────
  const fetchConversionsByUnit = async (unitId) => {
    setConvLoading(true);
    try {
      const res = await axios.get(
        `${baseUrl}/getConversions/${unitId}`,
        { headers: { Authorization: token } }
      );
      setConversions(res.data);
    } catch (err) {
      setConversions([]);
    } finally {
      setConvLoading(false);
    }
  };

  // ── API: Add unit ────────────────────────────────────────
  const handleAddUnit = async () => {
    if (!addForm.name.trim() || !addForm.symbol.trim()) {
      MySwal.fire("Warning", "Unit name and symbol are required!", "warning");
      return;
    }
    setAddLoading(true);
    try {
      await axios.post(
        `${baseUrl}/admin/addUnit`,
        {
          name: addForm.name.trim(),
          symbol: addForm.symbol.trim(),
        },
        { headers: { Authorization: token } }
      );
      MySwal.fire("Success", "Unit added successfully!", "success");
      setAddForm({ name: "", symbol: "" });
      setShowAddModal(false);
      fetchUnits();
    } catch (err) {
      if (err.response?.status === 403)
        MySwal.fire("Error", err.response.data || "Access Denied or Duplicate Unit!", "error");
      else
        MySwal.fire("Error", "Failed to add unit!", "error");
    } finally {
      setAddLoading(false);
    }
  };

  // ── API: Update unit ─────────────────────────────────────
  const handleUpdateUnit = async () => {
    if (!editForm.name.trim() || !editForm.symbol.trim()) {
      MySwal.fire("Warning", "Unit name and symbol are required!", "warning");
      return;
    }
    setEditLoading(true);
    try {
      await axios.put(
        `${baseUrl}/admin/updateUnit/${editUnit.id}`,
        {
          name: editForm.name.trim(),
          symbol: editForm.symbol.trim(),
        },
        { headers: { Authorization: token } }
      );
      MySwal.fire("Success", "Unit updated successfully!", "success");
      setShowEditModal(false);
      setEditUnit(null);
      fetchUnits();
    } catch (err) {
      if (err.response?.status === 403)
        MySwal.fire("Error", err.response.data || "Access Denied!", "error");
      else if (err.response?.status === 404)
        MySwal.fire("Error", "Unit not found or is a Default Unit!", "error");
      else if (err.response?.status === 409)
        MySwal.fire("Error", "Unit name already exists!", "error");
      else
        MySwal.fire("Error", "Failed to update unit!", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // ── API: Delete unit ─────────────────────────────────────
  const handleDeleteUnit = async (item) => {
    // Block delete for DEFAULT units
    if (isDefault(item)) {
      MySwal.fire("Cannot Delete", "This is a default unit and cannot be deleted!", "warning");
      return;
    }

    const confirm = await MySwal.fire({
      icon: "warning",
      title: "Delete Unit?",
      text: `Are you sure you want to delete "${item.name}"?`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#e03535",
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.delete(
        `${baseUrl}/admin/deleteUnit/${item.id}`,
        { headers: { Authorization: token } }
      );
      MySwal.fire("Deleted!", res.data || "Unit deleted successfully!", "success");
      if (selectedUnit?.id === item.id) setSelectedUnit(null);
      fetchUnits();
    } catch (err) {
      if (err.response?.status === 403)
        MySwal.fire("Error", err.response.data || "Unit is used by items, cannot delete!", "error");
      else if (err.response?.status === 404)
        MySwal.fire("Error", "Unit not found!", "error");
      else if (err.response?.status === 409)
        MySwal.fire("Error", err.response.data || "Unit is used in a conversion, cannot delete!", "error");
      else
        MySwal.fire("Error", "Failed to delete unit!", "error");
    }
  };

  // ── API: Add conversion ───────────────────────────────────
  const handleAddConversion = async () => {
    if (!convForm.secondaryUnitId || !convForm.rate || !convForm.baseRate) {
      MySwal.fire("Warning", "Base rate, secondary unit, and rate are required!", "warning");
      return;
    }
    setConvAddLoading(true);
    try {
      await axios.post(
        `${baseUrl}/admin/addConversion`,
        {
          baseUnitId: selectedUnit.id,
          baseRate: parseInt(convForm.baseRate),
          secondaryUnitId: parseInt(convForm.secondaryUnitId),
          rate: parseInt(convForm.rate),
        },
        { headers: { Authorization: token } }
      );
      MySwal.fire("Success", "Conversion added successfully!", "success");
      setConvForm({ baseRate: "1", secondaryUnitId: "", rate: "" });
      setShowConvModal(false);
      fetchConversionsByUnit(selectedUnit.id);
    } catch (err) {
      if (err.response?.status === 403)
        MySwal.fire("Error", err.response.data || "Access Denied or Invalid Units!", "error");
      else
        MySwal.fire("Error", "Failed to add conversion!", "error");
    } finally {
      setConvAddLoading(false);
    }
  };

  // ── API: Delete conversion ────────────────────────────────
  const handleDeleteConversion = async (conv) => {
    const confirm = await MySwal.fire({
      icon: "warning",
      title: "Delete Conversion?",
      text: `Remove "1 ${conv.baseUnitName} = ${conv.rate} ${conv.secondaryUnitName}"?`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#e03535",
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.delete(
        `${baseUrl}/admin/deleteConversion/${conv.id}`,
        { headers: { Authorization: token } }
      );
      MySwal.fire("Deleted!", res.data || "Conversion deleted successfully!", "success");
      fetchConversionsByUnit(selectedUnit.id);
    } catch (err) {
      if (err.response?.status === 403)
        MySwal.fire("Error", err.response.data || "Access Denied!", "error");
      else if (err.response?.status === 404)
        MySwal.fire("Error", err.response.data || "Conversion not found or is a Default Conversion!", "error");
      else
        MySwal.fire("Error", "Failed to delete conversion!", "error");
    }
  };

  // ── Tab navigation ───────────────────────────────────────
  const handleTabClick = (key) => {
    setActiveTab(key);
    if (key === "all")      navigate("/dashboard/viewitem");
    if (key === "category") navigate("/dashboard/category");
  };

  // ── Filtered lists ───────────────────────────────────────
  const filteredUnits = units.filter((u) =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Units available to pick as "Secondary Unit" — exclude the currently selected base unit itself
  const secondaryUnitOptions = units.filter((u) => u.id !== selectedUnit?.id);

  // ── Is DEFAULT unit / conversion ──────────────────────────
  const isDefault = (item) => item.company === "DEFAULT";

  return (
    <>
      <div className="page-header"></div>

      <div className="invoice-card-wrapper" style={{ marginLeft: "-20px", marginRight: "-25px" }}>
        <div>
          <div className="invoice-card">
            <div className="card-body p-0">

              {/* ══ TAB BAR ══ */}
              <div
                className="vc-tab-bar"
                style={{ marginLeft: "-25px", marginRight: "-25px", paddingLeft: "25px", paddingRight: "25px" }}
              >
                <div className="vc-tabs-left">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      className={`vc-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                      onClick={() => handleTabClick(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {role === "ADMIN" && (
                  <div className="vc-tab-actions">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setShowAddModal(true)}
                    >
                      <i className="feather icon-plus" style={{ marginRight: 5 }}></i>
                      Add Unit
                    </button>
                  </div>
                )}
              </div>

              {/* ══ ADD UNIT MODAL (Name + Symbol only) ══ */}
              {showAddModal && (
                <div className="modal-overlay">
                  <div className="modal-box" style={{ maxWidth: 600, width: "100%" }}>
                    <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 12, marginBottom: 16 }}>
                      <h5 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>NEW UNIT</h5>
                      <span onClick={() => { setShowAddModal(false); setAddForm({ name: "", symbol: "" }); }} style={{ cursor: "pointer", fontSize: 16 }}>✖</span>
                    </div>
                    <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <div style={{ display: "flex", gap: 16 }}>
                        {/* Unit Name */}
                        <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>UNIT NAME *</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Kilogram"
                            value={addForm.name}
                            onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                            style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderRadius: 0, paddingLeft: 0, fontSize: 14 }}
                          />
                        </div>
                        {/* Symbol */}
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>SYMBOL *</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. kg"
                            value={addForm.symbol}
                            onChange={(e) => setAddForm({ ...addForm, symbol: e.target.value })}
                            style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderRadius: 0, paddingLeft: 0, fontSize: 14 }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer" style={{ justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e2e8f0", paddingTop: 12, marginTop: 16 }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleAddUnit}
                        disabled={addLoading}
                      >
                        {addLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ══ EDIT UNIT MODAL (Name + Symbol only) ══ */}
              {showEditModal && (
                <div className="modal-overlay">
                  <div className="modal-box" style={{ maxWidth: 600, width: "100%" }}>
                    <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 12, marginBottom: 16 }}>
                      <h5 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>EDIT UNIT</h5>
                      <span onClick={() => { setShowEditModal(false); setEditUnit(null); }} style={{ cursor: "pointer", fontSize: 16 }}>✖</span>
                    </div>
                    <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <div style={{ display: "flex", gap: 16 }}>
                        <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>UNIT NAME *</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Kilogram"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderRadius: 0, paddingLeft: 0, fontSize: 14 }}
                          />
                        </div>
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>SYMBOL *</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. kg"
                            value={editForm.symbol}
                            onChange={(e) => setEditForm({ ...editForm, symbol: e.target.value })}
                            style={{ borderTop: "none", borderLeft: "none", borderRight: "none", borderRadius: 0, paddingLeft: 0, fontSize: 14 }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer" style={{ justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e2e8f0", paddingTop: 12, marginTop: 16 }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleUpdateUnit}
                        disabled={editLoading}
                      >
                        {editLoading ? "Updating..." : "Update"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ══ ADD CONVERSION MODAL (Vyapar-style) ══ */}
              {showConvModal && selectedUnit && (
                <div className="modal-overlay">
                  <div className="modal-box" style={{ maxWidth: 600, width: "100%" }}>
                    <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 12, marginBottom: 16 }}>
                      <h5 style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>ADD CONVERSION</h5>
                      <span
                        onClick={() => {
                          setShowConvModal(false);
                          setConvForm({ baseRate: "1", secondaryUnitId: "", rate: "" });
                        }}
                        style={{ cursor: "pointer", fontSize: 16 }}
                      >✖</span>
                    </div>
                    <div className="modal-body" style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                      {/* Base Unit (locked) */}
                      <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>BASE UNIT</label>
                        <div style={{ display: "flex", gap: 6 }}>
                          <input
                            type="number"
                            className="form-control"
                            min="1"
                            value={convForm.baseRate}
                            onChange={(e) => setConvForm({ ...convForm, baseRate: e.target.value })}
                            style={{ width: 60, fontSize: 14 }}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={`${selectedUnit.name} (${selectedUnit.symbol})`}
                            disabled
                            style={{ fontSize: 14, background: "#f1f5f9" }}
                          />
                        </div>
                      </div>

                      <span style={{ fontSize: 18, fontWeight: 600, paddingBottom: 8 }}>=</span>

                      {/* Rate */}
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>RATE</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="e.g. 500"
                          value={convForm.rate}
                          onChange={(e) => setConvForm({ ...convForm, rate: e.target.value })}
                          style={{ fontSize: 14 }}
                        />
                      </div>

                      {/* Secondary Unit dropdown */}
                      <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>SECONDARY UNIT</label>
                        <select
                          className="form-control"
                          value={convForm.secondaryUnitId}
                          onChange={(e) => setConvForm({ ...convForm, secondaryUnitId: e.target.value })}
                          style={{ fontSize: 14 }}
                        >
                          <option value="">Select unit</option>
                          {secondaryUnitOptions.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.symbol})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="modal-footer" style={{ justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e2e8f0", paddingTop: 12, marginTop: 16 }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleAddConversion}
                        disabled={convAddLoading}
                      >
                        {convAddLoading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="vc-wrapper">

                {/* ══ LEFT PANEL ══ */}
                <div className="vc-left-panel">
                  <div className="vc-left-header">
                    <div className="vc-search-box">
                      <i className="feather icon-search"></i>
                      <input
                        className="vc-search-input"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="vc-col-headers">
                    <span className="invoice-column-header-text" style={{ flex: 1 }}>
                      UNIT
                      <i className="fa-solid fa-filter ml-1" style={{ fontSize: "9px", color: "#e74c3c" }}></i>
                    </span>
                    <span className="invoice-column-header-text">ITEMS</span>
                  </div>

                  <div className="vc-course-list">
                    {loading && [...Array(4)].map((_, i) => (
                      <div key={i} className="vc-course-row" style={{ gap: 10 }}>
                        <div className="skeleton skeleton-title" style={{ flex: 1, height: 14 }}></div>
                        <div className="skeleton" style={{ width: 40, height: 20, borderRadius: 20 }}></div>
                      </div>
                    ))}

                    {!loading && filteredUnits.length === 0 && (
                      <div className="vc-list-message">No units found</div>
                    )}

                    {!loading && filteredUnits.map((item) => {
                      const active = selectedUnit?.id === item.id;
                      return (
                        <div
                          key={item.id}
                          className={`vc-course-row ${active ? "active" : ""}`}
                          onClick={() => setSelectedUnit(item)}
                        >
                          <span className="vc-course-row-name">{item.name || "—"}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {/* Item count badge */}
                            <span className="vc-price-badge paid" style={{ minWidth: 24, textAlign: "center" }}>
                              {item.itemCount ?? 0}
                            </span>

                            {/* 3-dot menu — ADMIN only */}
                            {role === "ADMIN" && (
                              <div
                                className="vc-row-menu-wrap"
                                ref={openMenuId === item.id ? menuRef : null}
                              >
                                <button
                                  className="vc-row-menu-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(openMenuId === item.id ? null : item.id);
                                  }}
                                >
                                  <i className="feather icon-more-vertical"></i>
                                </button>
                                {openMenuId === item.id && (
                                  <div className="vc-row-dropdown">
                                    <button onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(null);
                                      setEditUnit(item);
                                      setEditForm({
                                        name: item.name,
                                        symbol: item.symbol,
                                      });
                                      setShowEditModal(true);
                                    }}>
                                      <i className="feather icon-edit-2"></i> Edit
                                    </button>
                                    <button className="danger" onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(null);
                                      handleDeleteUnit(item);
                                    }}>
                                      <i className="feather icon-trash-2"></i> Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ══ RIGHT PANEL ══ */}
                <div className="vc-right-panel" style={{ overflow: "hidden", minWidth: 0 }}>

                  {/* Selected unit name bar */}
                  {selectedUnit && (
                    <div className="vc-detail-bar">
                      <div className="vc-detail-bar-top">
                        <div className="vc-course-title-large">
                          <span>{selectedUnit.name}</span>
                          <i className="feather icon-arrow-right" style={{ fontSize: 13, color: "#94a3b8" }}></i>
                          <span className="vc-paytype-tag">{selectedUnit.symbol}</span>
                        </div>

                        {/* Add Conversion button — ADMIN only */}
                        {role === "ADMIN" && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowConvModal(true)}
                          >
                            <i className="feather icon-plus" style={{ marginRight: 5 }}></i>
                            Add Conversion
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                    {/* ══ CONVERSION TABLE (many rows now) ══ */}
                    <div className="vc-students-section">
                      <div className="vc-students-header">
                        <span className="vc-students-title" style={{ fontSize: 15 }}>Units</span>
                      </div>

                      <div className="vc-table-wrap">
                        <table className="table table-hover table-bordered table-sm invoice-table">
                          <thead>
                            <tr>
                              <th style={{ width: 50 }}>#</th>
                              <th>Conversion</th>
                              {role === "ADMIN" && <th style={{ width: 80 }}></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {!selectedUnit && (
                              <tr>
                                <td colSpan={3} style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>
                                  Select a unit to view conversion
                                </td>
                              </tr>
                            )}

                            {selectedUnit && convLoading && (
                              <tr>
                                <td colSpan={3} style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>
                                  Loading...
                                </td>
                              </tr>
                            )}

                            {selectedUnit && !convLoading && conversions.length === 0 && (
                              <tr>
                                <td colSpan={3} style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>
                                  No conversion available
                                </td>
                              </tr>
                            )}

                            {selectedUnit && !convLoading && conversions.map((conv, index) => (
                              <tr key={conv.id}>
                                <td>{index + 1}</td>
                                <td>
                                  {conv.baseRate} {conv.baseUnitName} = {conv.rate} {conv.secondaryUnitName}
                                </td>
                                {role === "ADMIN" && (
                                  <td style={{ textAlign: "center" }}>
                                    {!isDefault(conv) && (
                                      <button
                                        className="vc-row-menu-btn"
                                        title="Delete conversion"
                                        onClick={() => handleDeleteConversion(conv)}
                                      >
                                        <i className="feather icon-trash-2" style={{ color: "#e03535" }}></i>
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Units;